import { useCallback, useMemo, useSyncExternalStore } from 'react';
import {
  PRESET_LABELS,
  STRINGS,
  type StringKey,
  type UiLocale,
} from './strings.ts';

const LOCALE_KEY = 'rikosub:uiLocale';

function detectNavigatorLocale(): UiLocale {
  const nav = (typeof navigator !== 'undefined' ? navigator.language : 'en').toLowerCase();
  if (nav === 'zh-cn' || nav === 'zh-sg' || nav.startsWith('zh-hans') || nav === 'zh') {
    return 'zh-CN';
  }
  if (nav.startsWith('zh')) return 'zh-TW';
  if (nav.startsWith('ja')) return 'ja';
  if (nav.startsWith('ko')) return 'ko';
  return 'en';
}

function readStoredLocale(): UiLocale {
  try {
    const raw = localStorage.getItem(LOCALE_KEY);
    if (raw === 'en' || raw === 'zh-TW' || raw === 'zh-CN' || raw === 'ja' || raw === 'ko') {
      return raw;
    }
  } catch {
    // ignore
  }
  return detectNavigatorLocale();
}

function writeLocale(locale: UiLocale): void {
  try {
    localStorage.setItem(LOCALE_KEY, locale);
  } catch {
    // ignore
  }
}

let currentLocale: UiLocale = readStoredLocale();
const listeners = new Set<() => void>();

function emit(): void {
  for (const cb of [...listeners]) cb();
}

export function getUiLocale(): UiLocale {
  return currentLocale;
}

export function setUiLocale(locale: UiLocale): void {
  if (currentLocale === locale) return;
  currentLocale = locale;
  writeLocale(locale);
  emit();
}

export function subscribeUiLocale(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function t(key: StringKey, locale: UiLocale = currentLocale): string {
  return STRINGS[locale][key] ?? STRINGS.en[key] ?? key;
}

export function presetLabel(id: string, locale: UiLocale = currentLocale): string {
  return PRESET_LABELS[locale][id] ?? id;
}

/** Display name for a BCP-47 language code (Intl.DisplayNames fallback). */
function intlLocaleForUi(locale: UiLocale): string {
  if (locale === 'zh-TW') return 'zh-TW';
  if (locale === 'zh-CN') return 'zh-CN';
  if (locale === 'ja') return 'ja';
  if (locale === 'ko') return 'ko';
  return 'en';
}

export function languageDisplayName(code: string, locale: UiLocale = currentLocale): string {
  try {
    const dn = new Intl.DisplayNames([intlLocaleForUi(locale)], {
      type: 'language',
    });
    return dn.of(code) ?? code;
  } catch {
    return code;
  }
}

export function useI18n(): {
  locale: UiLocale;
  setLocale: (locale: UiLocale) => void;
  t: (key: StringKey) => string;
  presetLabel: (id: string) => string;
} {
  const locale = useSyncExternalStore(subscribeUiLocale, getUiLocale, () => 'en' as UiLocale);
  const setLocale = useCallback((next: UiLocale) => {
    setUiLocale(next);
  }, []);
  return useMemo(
    () => ({
      locale,
      setLocale,
      t: (key: StringKey) => t(key, locale),
      presetLabel: (id: string) => presetLabel(id, locale),
    }),
    [locale, setLocale],
  );
}

/** Test helper: reset locale without touching other listeners permanently. */
export function resetUiLocaleForTests(locale: UiLocale = 'en'): void {
  currentLocale = locale;
  writeLocale(locale);
  emit();
}
